import { useCallback, useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { hasStorageAvailable } from '@klaviyo-mkt/gatsby-components/shared/utils/storage';
import { $skilljarUser } from '@academy/skilljar/stores/user';

import type { IStoredUserData, IUserCourseData } from '../../skilljar/types';

const MAX_AGE = 60;
const STORAGE_KEY = 'skilljarUserData';

const fallback = {
  data: {
    course_path_progress: [],
    course_progress: [],
    recommendations: [],
  },
  date: 0,
};

const fetchUserData = async (userId: string): Promise<IUserCourseData> => {
  const endpoint = `https://www.klaviyo.com/ajax/academy/progress/${userId}`;
  let cachedData: string | null = null;

  if (!userId) {
    return fallback.data;
  }

  try {
    if (hasStorageAvailable('sessionStorage')) {
      cachedData = sessionStorage.getItem(STORAGE_KEY);

      if (cachedData) {
        const parsedData: IStoredUserData = JSON.parse(cachedData);
        if (parsedData.date) {
          const now = Date.now();
          const diffSeconds = (now - parsedData.date) / 1000;
          if (diffSeconds <= MAX_AGE) {
            return parsedData.data;
          }
        }
      }

      const response = await fetch(endpoint, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Error fetching user data: ${response.status} ${response.statusText}`);
      }

      const data: { data: IUserCourseData; } = await response.json();
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ data: data.data, date: Date.now() }));

      return data.data;
    }

    throw new Error('Error: could not access session storage');

  } catch (error) {
    console.error('Error fetching user data:', error);

    if (cachedData) {
      return JSON.parse(cachedData).data;
    }

    return fallback.data;
  }
};

export const useGetUserCourseData = (): IUserCourseData => {
  const skilljarUser = useStore($skilljarUser);
  const userId = skilljarUser?.id;

  const [userCourseData, setUserCourseData] = useState<IUserCourseData>({
    course_path_progress: [],
    course_progress: [],
    recommendations: [],
  });

  const loadData = useCallback(async () => {
    if (!userId) {
      return null;
    }

    try {
      const data = await fetchUserData(userId);
      setUserCourseData(data);
    } catch (e) {
      console.log('Error fetching user data:', e);
      return null;
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [loadData, userId]);

  return userCourseData;
};
